import { useEffect, useState } from "react";
import { useActivityManifest } from "../../hooks/useActivityManifest.js";
import { useUpstreamOutputs } from "../../hooks/useUpstreamOutputs.js";
import type { StepNodeData } from "../../lib/yaml-serializer.js";
import { SchemaForm } from "./SchemaForm.js";

interface StepConfigPanelProps {
	data: StepNodeData;
	onChange: (data: Partial<StepNodeData>) => void;
	onDelete: () => void;
	nodes?: Array<{ id: string; data: { stepType?: string } }>;
	edges?: Array<{ source: string; target: string }>;
}

const STEP_ID_REGEX = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/;

export function StepConfigPanel({ data, onChange, onDelete, nodes, edges }: StepConfigPanelProps) {
	const [configJson, setConfigJson] = useState(JSON.stringify(data.config, null, "\t"));
	const [configError, setConfigError] = useState<string | null>(null);
	const [stepIdError, setStepIdError] = useState<string | null>(null);

	const { manifest, loading } = useActivityManifest(data.stepType);
	const configSchema = manifest?.config_schema;
	const templateVariables = useUpstreamOutputs(data.stepId, nodes ?? [], edges ?? []);

	// Re-sync textarea when data.config changes and schema form is not active
	useEffect(() => {
		if (!configSchema) {
			setConfigJson(JSON.stringify(data.config, null, "\t"));
			setConfigError(null);
		}
	}, [data.config, configSchema]);

	const handleStepIdChange = (value: string) => {
		onChange({ stepId: value });
		if (value && !STEP_ID_REGEX.test(value)) {
			setStepIdError("Must be lowercase alphanumeric with hyphens");
		} else {
			setStepIdError(null);
		}
	};

	const handleConfigChange = (value: string) => {
		setConfigJson(value);
		try {
			const parsed = JSON.parse(value);
			setConfigError(null);
			onChange({ config: parsed });
		} catch {
			setConfigError("Invalid JSON");
		}
	};

	const handleSchemaFormChange = (value: Record<string, unknown>) => {
		onChange({ config: value });
	};

	return (
		<div className="flex h-full flex-col overflow-y-auto p-4">
			<div className="mb-3 text-xs font-semibold uppercase tracking-widest text-text-muted">
				Step Configuration
			</div>
			<div className="mb-1 text-sm font-semibold text-text-primary">{data.stepId}</div>
			<div className="mb-4 text-xs text-text-muted">{data.stepType}</div>

			{/* Step ID */}
			<label className="mb-3 block">
				<span className="text-xs text-text-muted">Step ID</span>
				<input
					type="text"
					value={data.stepId}
					onChange={(e) => handleStepIdChange(e.target.value)}
					className="mt-1 block w-full rounded-xl border border-border bg-canvas px-3 py-2 text-xs text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
				/>
				{stepIdError && <span className="mt-1 text-xs text-error">{stepIdError}</span>}
			</label>

			{/* Config: schema form or JSON textarea */}
			{loading ? (
				<div className="mb-3 text-xs text-text-muted">Loading config schema…</div>
			) : configSchema ? (
				<div className="mb-3 border-t border-border pt-3">
					<span className="mb-2 block text-xs font-semibold uppercase tracking-widest text-text-muted">
						Activity Config
					</span>
					<SchemaForm
						schema={configSchema}
						value={(data.config as Record<string, unknown>) ?? {}}
						onChange={handleSchemaFormChange}
						templateVariables={templateVariables}
					/>
				</div>
			) : (
				<label className="mb-3 block">
					<span className="text-xs text-text-muted">Config (JSON)</span>
					<textarea
						value={configJson}
						onChange={(e) => handleConfigChange(e.target.value)}
						rows={8}
						className="mt-1 block w-full rounded-xl border border-border bg-canvas px-3 py-2 font-mono text-xs text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
					/>
					{configError && <span className="mt-1 text-xs text-error">{configError}</span>}
				</label>
			)}

			{/* Condition */}
			<label className="mb-3 block">
				<span className="text-xs text-text-muted">Condition</span>
				<input
					type="text"
					value={data.condition ?? ""}
					onChange={(e) => onChange({ condition: e.target.value || undefined })}
					placeholder="{{steps.x.output.ready}}"
					className="mt-1 block w-full rounded-xl border border-border bg-canvas px-3 py-2 font-mono text-xs text-text-primary placeholder:text-text-muted/50 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
				/>
			</label>

			{/* Timeout */}
			<label className="mb-3 block">
				<span className="text-xs text-text-muted">Timeout</span>
				<input
					type="text"
					value={data.timeout ?? ""}
					onChange={(e) => onChange({ timeout: e.target.value || undefined })}
					placeholder="30s"
					className="mt-1 block w-full rounded-xl border border-border bg-canvas px-3 py-2 text-xs text-text-primary placeholder:text-text-muted/50 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
				/>
			</label>

			{/* Retry Policy */}
			<div className="mb-3 border-t border-border pt-3">
				<span className="text-xs font-semibold uppercase tracking-widest text-text-muted">
					Retry Policy
				</span>
				<div className="mt-2 grid grid-cols-2 gap-2">
					<label className="block">
						<span className="text-xs text-text-muted">Max Attempts</span>
						<input
							type="number"
							value={data.retry?.max_attempts ?? ""}
							onChange={(e) => {
								const val = e.target.value ? Number(e.target.value) : undefined;
								if (val !== undefined) {
									onChange({
										retry: {
											...data.retry,
											max_attempts: val,
											backoff: data.retry?.backoff ?? "exponential",
										},
									});
								} else {
									onChange({ retry: undefined });
								}
							}}
							className="mt-1 block w-full rounded-xl border border-border bg-canvas px-2 py-1.5 text-xs text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
						/>
					</label>
					<label className="block">
						<span className="text-xs text-text-muted">Backoff</span>
						<select
							value={data.retry?.backoff ?? "exponential"}
							onChange={(e) =>
								onChange({
									retry: {
										max_attempts: data.retry?.max_attempts ?? 3,
										backoff: e.target.value as "fixed" | "exponential",
										initial_interval: data.retry?.initial_interval,
										max_interval: data.retry?.max_interval,
									},
								})
							}
							className="mt-1 block w-full rounded-xl border border-border bg-canvas px-2 py-1.5 text-xs text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
						>
							<option value="exponential">Exponential</option>
							<option value="fixed">Fixed</option>
						</select>
					</label>
					<label className="block">
						<span className="text-xs text-text-muted">Initial Interval</span>
						<input
							type="text"
							value={data.retry?.initial_interval ?? ""}
							onChange={(e) =>
								onChange({
									retry: {
										max_attempts: data.retry?.max_attempts ?? 3,
										backoff: data.retry?.backoff ?? "exponential",
										initial_interval: e.target.value || undefined,
										max_interval: data.retry?.max_interval,
									},
								})
							}
							placeholder="2s"
							className="mt-1 block w-full rounded-xl border border-border bg-canvas px-2 py-1.5 text-xs text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
						/>
					</label>
					<label className="block">
						<span className="text-xs text-text-muted">Max Interval</span>
						<input
							type="text"
							value={data.retry?.max_interval ?? ""}
							onChange={(e) =>
								onChange({
									retry: {
										max_attempts: data.retry?.max_attempts ?? 3,
										backoff: data.retry?.backoff ?? "exponential",
										initial_interval: data.retry?.initial_interval,
										max_interval: e.target.value || undefined,
									},
								})
							}
							placeholder="30s"
							className="mt-1 block w-full rounded-xl border border-border bg-canvas px-2 py-1.5 text-xs text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
						/>
					</label>
				</div>
			</div>

			{/* Delete */}
			<button
				type="button"
				onClick={onDelete}
				className="mt-auto rounded-xl border border-error/30 px-3 py-2 text-xs text-error transition-colors hover:bg-error/10"
			>
				Delete Step
			</button>
		</div>
	);
}
